<template>
  <div class="transfer-teams-from-grid">
    <!--  ЧТО ПЕРЕНОСИМ  -->
    <section class="picked-teams">
      <div class="section-title">
        <span>Переносим</span>
        <span class="badge-count">{{ teams.length }}</span>
      </div>
      <div class="team-chips">
        <span v-for="team in teams" :key="`transfer-team-${ team.id }`" class="team-chip">
          {{ team.name }}
        </span>
      </div>
    </section>

    <!--  КУДА ПЕРЕНОСИМ  -->
    <section class="destination">
      <div class="column">
        <div class="column-header">
          <span class="column-index">1</span>
          Этап
        </div>
        <div class="option-list">
          <button v-for="stage in stages"
                  :key="`transfer-stage-${ stage.id }`"
                  type="button"
                  class="option"
                  :class="{ active: selectedStageId === stage.id }"
                  @click="selectedStageId = stage.id">
            <span class="option-name">{{ stage.name }}</span>
            <span class="option-meta">
              <span v-if="stage.id === params.currentStageId" class="tag current">текущий</span>
              <span class="tag">{{ stageFormatTitle(stage) }}</span>
            </span>
          </button>
          <p v-if="!stages.length" class="empty-text">Этапов нет</p>
        </div>
      </div>

      <div class="column" v-if="hasRoundsStep">
        <div class="column-header">
          <span class="column-index">2</span>
          Раунд
          <span v-if="isRoundRequired" class="required">обязательно</span>
        </div>
        <div class="option-list">
          <template v-for="group in roundGroupsByType">
            <div v-if="group.rounds.length" :key="`round-type-${ group.type }`" class="option-group">
              <div class="option-group-title">{{ group.title }}</div>
              <button v-for="round in group.rounds"
                      :key="`transfer-round-${ round.id }`"
                      type="button"
                      class="option"
                      :class="{ active: selectedRoundId === round.id }"
                      @click="toggleRound(round.id)">
                <span class="option-name">{{ round.name }}</span>
                <span class="option-meta">
                  <span class="tag">{{ (round.groups || []).length }} гр.</span>
                </span>
              </button>
            </div>
          </template>
          <p v-if="!selectedStageId" class="empty-text">Сначала выберите этап</p>
          <p v-else-if="!rounds.length" class="empty-text">Раундов нет</p>
        </div>
      </div>

      <div class="column">
        <div class="column-header">
          <span class="column-index">{{ hasRoundsStep ? 3 : 2 }}</span>
          Группа
          <span v-if="isGroupRequired" class="required">обязательно</span>
        </div>
        <div class="option-list">
          <button v-for="group in groups"
                  :key="`transfer-group-${ group.id }`"
                  type="button"
                  class="option"
                  :class="{ active: selectedGroupId === group.id }"
                  @click="toggleGroup(group.id)">
            <span class="option-name">{{ group.name }}</span>
          </button>
          <p v-if="!selectedStageId" class="empty-text">Сначала выберите этап</p>
          <p v-else-if="hasRoundsStep && !selectedRoundId" class="empty-text">Сначала выберите раунд</p>
          <p v-else-if="!groups.length" class="empty-text">Групп нет — команды попадут в раунд</p>
        </div>
      </div>
    </section>

    <!--  МАРШРУТ  -->
    <div class="route" :class="{ ready: !!selectedStageId }">
      <span class="route-label">Маршрут:</span>
      <template v-if="selectedStageId">
        <span class="route-point">{{ selectedStageName }}</span>
        <template v-if="selectedRoundName">
          <span class="route-arrow">&rarr;</span>
          <span class="route-point">{{ selectedRoundName }}</span>
        </template>
        <template v-if="selectedGroupName">
          <span class="route-arrow">&rarr;</span>
          <span class="route-point">{{ selectedGroupName }}</span>
        </template>
      </template>
      <span v-else class="route-placeholder">выберите этап назначения</span>
    </div>

    <b-alert v-if="isSameStage" class="mt-3 mb-0 custom-alert" show variant="warning">
      Вы переносите команды в тот же этап, из которого они сейчас. Проверьте выбор.
    </b-alert>
    <b-alert v-if="missingStepText" class="mt-3 mb-0 custom-alert" show variant="info">
      {{ missingStepText }}
    </b-alert>
    <b-alert v-if="errorText" class="mt-3 mb-0 custom-alert" show variant="danger">{{ errorText }}</b-alert>

    <footer>
      <b-button variant="link" class="cancel-button" :disabled="isLoadingSubmit" @click="$emit('closeModal')">
        Отмена
      </b-button>
      <b-button :disabled="isDisabledSubmit"
                @click="submit"
                class="submit-button"
                variant="primary">
        <b-spinner v-if="isLoadingSubmit" small class="mr-2" />
        {{ isLoadingSubmit ? 'Переносим...' : `Перенести ${ teams.length } ${ teamsWord }` }}
      </b-button>
    </footer>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import api from "@/api/api";
import { stageTypesById, typeRounds } from "@/common/tournamentCommon";

export default {
  name: "TransferTeamsFromGrid",
  props: {
    params: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedStageId: null,
      selectedRoundId: null,
      selectedGroupId: null,
      isLoadingSubmit: false,
      errorText: ''
    }
  },
  computed: {
    ...mapState('moduleNewTournament', ['tournament']),
    ...mapGetters('moduleNewTournament', ['stages']),
    teams() {
      return this.params.teams || []
    },
    teamsWord() {
      const count = this.teams.length
      const lastTwo = count % 100
      const last = count % 10
      if (lastTwo > 10 && lastTwo < 20) return 'команд'
      if (last === 1) return 'команду'
      if (last > 1 && last < 5) return 'команды'
      return 'команд'
    },
    selectedStage() {
      return this.stages.find(({ id }) => id === this.selectedStageId) || null
    },
    formatBySelectedStage() {
      return stageTypesById.get(this.selectedStage?.format_id)?.name || false
    },
    // У групповой стадии раундов нет — команды переносятся сразу в группу
    hasRoundsStep() {
      return this.formatBySelectedStage !== 'olympic'
    },
    // Раунд и группу требуем только тогда, когда у этапа реально есть куда переносить
    isRoundRequired() {
      return this.hasRoundsStep && this.rounds.length > 0
    },
    isGroupRequired() {
      return this.groups.length > 0
    },
    missingStepText() {
      if (!this.selectedStageId) return ''
      if (this.isRoundRequired && !this.selectedRoundId) return 'Выберите раунд, в который переносим команды.'
      if (this.isGroupRequired && !this.selectedGroupId) return 'Выберите группу, в которую переносим команды.'
      return ''
    },
    rounds() {
      return this.selectedStage?.stage_rounds || []
    },
    roundGroupsByType() {
      return typeRounds.map(({ type, title }) => ({
        type,
        title,
        rounds: this.rounds.filter((round) => round.type === type)
      }))
    },
    groups() {
      if (!this.hasRoundsStep) return this.selectedStage?.groups || []
      if (!this.selectedRoundId) return []
      return this.rounds.find(({ id }) => id === this.selectedRoundId)?.groups || []
    },
    selectedStageName() {
      return this.selectedStage?.name || ''
    },
    selectedRoundName() {
      return this.rounds.find(({ id }) => id === this.selectedRoundId)?.name || ''
    },
    selectedGroupName() {
      return this.groups.find(({ id }) => id === this.selectedGroupId)?.name || ''
    },
    isSameStage() {
      return !!this.selectedStageId && this.selectedStageId === this.params.currentStageId
    },
    isDisabledSubmit() {
      if (!this.selectedStageId || !this.teams.length || this.isLoadingSubmit) return true
      if (this.isRoundRequired && !this.selectedRoundId) return true
      if (this.isGroupRequired && !this.selectedGroupId) return true
      return false
    }
  },
  methods: {
    stageFormatTitle(stage) {
      return stageTypesById.get(stage.format_id)?.title || 'Без формата'
    },
    autoSelectSingleGroup() {
      this.$nextTick(() => {
        if (this.groups.length === 1) this.selectedGroupId = this.groups[0].id
      })
    },
    toggleRound(roundId) {
      this.selectedRoundId = this.selectedRoundId === roundId ? null : roundId
    },
    toggleGroup(groupId) {
      this.selectedGroupId = this.selectedGroupId === groupId ? null : groupId
    },
    async submit() {
      try {
        this.errorText = ''
        this.isLoadingSubmit = true

        await api.tournament.newTransferTeamsToNextStage({
          ...(this.selectedGroupId && { group_id: this.selectedGroupId }),
          ...(this.selectedRoundId && { round_id: this.selectedRoundId }),
          stage_id: this.selectedStageId,
          stage_team_ids: this.teams.map(({ stage_team_id }) => stage_team_id)
        })

        await this.$store.dispatch('moduleNewTournament/getTournament', {
          id: this.$route.params.standingId
        })

        this.$bvToast.toast(`Перенесено команд: ${ this.teams.length } → ${ this.selectedStageName }`, {
          title: 'Перенос выполнен',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        })

        this.$emit('submit')
        this.$emit('closeModal')
      } catch (e) {
        console.log('e ', { e })
        this.errorText = e?.response?.data?.message || 'Не удалось перенести команды'
      } finally {
        this.isLoadingSubmit = false
      }
    }
  },
  watch: {
    selectedStageId() {
      this.selectedRoundId = null
      this.selectedGroupId = null
      this.errorText = ''
      // если раунд один — выбираем его сразу, чтобы не кликать лишний раз
      if (this.hasRoundsStep && this.rounds.length === 1) this.selectedRoundId = this.rounds[0].id
      else this.autoSelectSingleGroup()
    },
    selectedRoundId() {
      this.selectedGroupId = null
      this.autoSelectSingleGroup()
    }
  }
}
</script>

<style scoped lang="scss">
.transfer-teams-from-grid {
  .section-title {
    display: flex;
    align-items: center;
    gap: 8px;

    font-size: 12px;
    font-weight: 600;
    color: #7d7d8d;
    text-transform: uppercase;
    letter-spacing: .04em;
    margin-bottom: 10px;

    .badge-count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 22px;
      height: 22px;
      padding: 0 6px;
      border-radius: 50px;
      background: #7366ff;
      color: white;
      font-size: 11px;
    }
  }

  .picked-teams {
    .team-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      max-height: 92px;
      overflow-y: auto;

      .team-chip {
        padding: 5px 10px;
        border-radius: 50px;
        background: #f4f2ff;
        border: 1px solid #ded9ff;
        color: #4d43b5;
        font-size: 11px;
        font-weight: 600;
      }
    }
  }

  .destination {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 14px;
    margin-top: 22px;

    .column {
      border: 1px solid #eceaf5;
      border-radius: 12px;
      padding: 12px;
      background: #fbfbfe;
    }

    .column-header {
      display: flex;
      align-items: center;
      gap: 8px;

      font-size: 13px;
      font-weight: 600;
      color: #2f2f41;
      margin-bottom: 10px;

      .column-index {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #e6e3ff;
        color: #4d43b5;
        font-size: 11px;
      }

      .optional {
        margin-left: auto;
        font-size: 10px;
        font-weight: 400;
        color: #a5a5b5;
      }

      .required {
        margin-left: auto;
        padding: 2px 7px;
        border-radius: 50px;
        background: #fff0d6;
        color: #a97400;
        font-size: 9px;
        font-weight: 600;
      }
    }

    .option-list {
      max-height: 240px;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .option-group {
      display: flex;
      flex-direction: column;
      gap: 4px;

      &:not(:first-child) {
        margin-top: 8px;
      }

      .option-group-title {
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: .04em;
        color: #a5a5b5;
      }
    }

    .option {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      width: 100%;
      text-align: left;

      padding: 9px 12px;
      border: 1px solid transparent;
      border-radius: 10px;
      background: white;
      font-size: 12px;
      color: #2f2f41;
      transition: .15s;

      &:hover {
        background: #f0eeff;
        color: #4d43b5;
      }

      &.active {
        background: #7366ff;
        color: white;
        box-shadow: 0 4px 12px rgba(115, 102, 255, .35);

        .tag {
          background: rgba(255, 255, 255, .22);
          color: white;
        }
      }

      .option-name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .option-meta {
        display: flex;
        align-items: center;
        gap: 4px;
        flex: 0 0 auto;
      }

      .tag {
        padding: 2px 7px;
        border-radius: 50px;
        background: #f1f0f7;
        color: #8b8b9c;
        font-size: 9px;
        white-space: nowrap;

        &.current {
          background: #fff0d6;
          color: #a97400;
        }
      }
    }

    .empty-text {
      margin: 8px 0;
      text-align: center;
      font-size: 12px;
      color: #a5a5b5;
    }
  }

  .route {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    margin-top: 18px;
    padding: 12px 14px;
    border-radius: 12px;
    border: 1px dashed #ded9ff;
    background: #fbfbfe;
    font-size: 12px;

    &.ready {
      border-style: solid;
      background: #f4f2ff;
    }

    .route-label {
      color: #a5a5b5;
    }

    .route-point {
      font-weight: 600;
      color: #4d43b5;
    }

    .route-arrow {
      color: #b7b1ff;
    }

    .route-placeholder {
      color: #a5a5b5;
      font-style: italic;
    }
  }

  .custom-alert {
    font-size: 12px;
    border-radius: 12px;
  }

  footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;

    border-top: 1px solid #dee2e6;
    padding-top: 15px;
    margin-top: 20px;
  }

  .cancel-button {
    color: #7d7d8d;
    font-size: 13px;
  }

  .submit-button {
    display: flex;
    align-items: center;
    background: #7366ff;
    border-color: #7366ff;
    border-radius: 12px;
    padding: 9px 20px;
    font-size: 13px;
  }
}
</style>
